import React, { FC } from 'react';
import { Box, Button, IconButton, Paper, Tooltip } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import NumberField from 'components/number-field';
import { TitleWithValue } from 'components/title-with-value';
import { formatMoney } from 'utils/helpers';
import { useGeneralChargesContext } from 'contexts/GeneralChargesContext';
import { ListingEntry } from 'graphql/listings/queries';
import { ChargeFormEntry } from 'views/orders/charges/components/GeneralChargesTable/types';
import { ChargesFormType } from 'views/orders/charges/components/GeneralChargesTable/hooks';
import { AmountField } from 'views/orders/charges/components/GeneralChargesTable/components/ChargesTableRow/components/Fields/AmountField';
import { PayeeField } from 'views/orders/charges/components/GeneralChargesTable/components/ChargesTableRow/components/Fields/PayeeField';
import { TaxField } from 'views/orders/charges/components/GeneralChargesTable/components/ChargesTableRow/components/Fields/TaxField';
import { ChargeDescriptionCard } from './ChargeDescriptionCard';
import { ChargeEndorsementCodeCard } from './ChargeEndorsementCodeCard';
import { PartiesInvolvedSection } from './PartiesInvolvedSection';

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  numberBadge: {
    width: '30px',
    height: '30px',
    backgroundColor: theme.palette.secondary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '5px',
    color: theme.palette.primary.dark,
  },
  manualNumberInput: {
    '& .MuiOutlinedInput-root': {
      width: '35px',
      height: '35px',
      backgroundColor: theme.palette.secondary.main,
      borderRadius: '5px',
      color: theme.palette.primary.dark,

      '& .MuiOutlinedInput-notchedOutline': {
        border: 'unset',
      },
      '& .MuiOutlinedInput-input ': {
        padding: `${theme.spacing(0.25)}`,
        textAlign: 'center',
      },
    },
  },
  byOthersRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  expandControlRow: {
    display: 'flex',
  },
  expandControl: {
    flex: 1,
    justifyContent: 'space-between',
    textTransform: 'none',
    padding: theme.spacing(1, 1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: '5px',
    backgroundColor: theme.palette.background.default,
    color: theme.palette.primary.dark,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
}));

interface Props {
  charge: ChargeFormEntry;
  index: number;
  currentLetter?: string;
  lettersPerMonth: string[];
  textPredectiveCharges: ListingEntry[];
  hideTax: boolean;
  isOrderTypeNotHUD1: boolean;
  onDeleteCharge: (rowIndex: number) => void;
  onEditSplitAmount: (charge: ChargeFormEntry, kind: 'Payee' | 'Payer' | 'Tax') => void;
  isPartiesInvolvedRowOpen: boolean;
  togglePartiesinvolvedRowOpen: () => void;
  onChargeAmountChange: (newAmount: number | null) => void;
  onChargePaidByAmountChange: () => void;
  onChargeNumberOfMonthsChange: (newMonths: number) => void;
  onPerMonthChange: (newPerMonthAmount: number) => void;
}

export const ChargeCard: FC<Props> = (props) => {
  const {
    charge,
    index,
    textPredectiveCharges,
    currentLetter,
    lettersPerMonth,
    hideTax,
    isOrderTypeNotHUD1,
    onDeleteCharge,
    onEditSplitAmount,
    isPartiesInvolvedRowOpen,
    togglePartiesinvolvedRowOpen,
    onChargeAmountChange,
    onChargePaidByAmountChange,
    onChargeNumberOfMonthsChange,
    onPerMonthChange,
  } = props;
  const { t } = useTranslation();
  const { getValues, control } = useFormContext<ChargesFormType>();
  const { hideEndorsementCode } = useGeneralChargesContext();
  const classes = useStyles();

  const showOverrideIndicator =
    charge.kind !== 'manual' &&
    charge.amount_calculated !== 0 &&
    charge.amount_override !== 0 &&
    charge.amount_calculated !== charge.amount_override;

  const handleDeleteCharge = () => {
    onDeleteCharge(index);
  };

  return (
    <Paper className={classes.card}>
      <Box className={classes.headerRow}>
        <Box className={classes.headerActions}>
          {charge.kind === 'manual' && (
            <IconButton aria-label="delete row" size="small" onClick={handleDeleteCharge}>
              <FontAwesomeIcon icon="trash-alt" size="sm" />
            </IconButton>
          )}
        </Box>

        {charge.kind === 'manual' ? (
          <Controller
            name={`charges.${index}.number`}
            control={control}
            render={({ field: { ref, onChange, ...field } }) => (
              <NumberField
                {...field}
                customRef={ref}
                color="secondary"
                className={classes.manualNumberInput}
                variant="outlined"
                onValueChange={(prop) => {
                  onChange(prop.floatValue);
                }}
                value={getValues(`charges.${index}.number`)}
              />
            )}
          />
        ) : (
          <Box className={classes.numberBadge}>
            {`${(hideEndorsementCode || (charge.letter === "")) ? "" : `${charge.letter}-`}${charge.number}`}
          </Box>
        )}
      </Box>

      {hideEndorsementCode === false && (
        <TitleWithValue title={t('charges:endorsement')}>
          <ChargeEndorsementCodeCard charge={charge} />
        </TitleWithValue>
      )}

      <TitleWithValue title={t('charges:description')}>
        <ChargeDescriptionCard
          index={index}
          charge={charge}
          textPredectiveCharges={textPredectiveCharges}
        />
      </TitleWithValue>

      <TitleWithValue title={t('charges:payee')}>
        <PayeeField charge={charge} onOpenSplitModal={() => onEditSplitAmount(charge, 'Payee')} />
      </TitleWithValue>

      <TitleWithValue title={t('accounting:amount')}>
        <Tooltip
          title={`Original calculation: ${formatMoney(charge.amount_calculated)}`}
          arrow
          placement="top"
          disableFocusListener={!showOverrideIndicator}
          disableHoverListener={!showOverrideIndicator}
          disableTouchListener={!showOverrideIndicator}
        >
          <div>
            <AmountField
              {...props}
              name="amount"
              disabled={charge.disabled}
              onAmountChange={onChargeAmountChange}
              borderColor={showOverrideIndicator ? 'lightyellow' : undefined}
            />
          </div>
        </Tooltip>
      </TitleWithValue>

      {currentLetter && lettersPerMonth.includes(currentLetter) && (
        <>
          <TitleWithValue title={t('charges:per-month')}>
            <AmountField
              {...props}
              name="per_month"
              disabled={charge.disabled}
              onAmountChange={(value) => onPerMonthChange(value || 0)}
            />
          </TitleWithValue>

          <TitleWithValue title={t('charges:number-of-month')}>
            <Controller
              name={`charges.${index}.months`}
              control={control}
              disabled={charge.disabled}
              render={({ field: { ref, onChange, ...field } }) => (
                <NumberField
                  {...field}
                  customRef={ref}
                  color="secondary"
                  variant="outlined"
                  disabled={field.disabled}
                  onValueChange={(prop) => {
                    onChange(prop.floatValue);
                    onChargeNumberOfMonthsChange(prop.floatValue || 0);
                  }}
                  value={getValues(`charges.${index}.months`)}
                />
              )}
            />
          </TitleWithValue>
        </>
      )}

      {!hideTax && (
        <TitleWithValue title="Tax">
          <TaxField charge={charge} onOpenSplitModal={() => onEditSplitAmount(charge, 'Tax')} />
        </TitleWithValue>
      )}

      <TitleWithValue
        title={
          isOrderTypeNotHUD1
            ? `${t('charges:paid-by-borrower')} - ${t('charges:at-closing')}`
            : t('charges:paidByBorrower')
        }
      >
        <AmountField
          {...props}
          name="buyer_at"
          disabled={charge.disabled}
          onAmountChange={onChargePaidByAmountChange}
        />
      </TitleWithValue>

      {isOrderTypeNotHUD1 && (
        <TitleWithValue title={`${t('charges:paid-by-borrower')} - ${t('charges:before-closing')}`}>
          <AmountField
            {...props}
            name="buyer_before"
            disabled={charge.disabled}
            onAmountChange={onChargePaidByAmountChange}
          />
        </TitleWithValue>
      )}

      <TitleWithValue
        title={
          isOrderTypeNotHUD1
            ? `${t('charges:paid-by-seller')} - ${t('charges:at-closing')}`
            : t('charges:paidBySeller')
        }
      >
        <AmountField
          {...props}
          name="seller_at"
          disabled={charge.disabled}
          onAmountChange={onChargePaidByAmountChange}
        />
      </TitleWithValue>

      {isOrderTypeNotHUD1 && (
        <TitleWithValue title={`${t('charges:paid-by-seller')} - ${t('charges:before-closing')}`}>
          <AmountField
            {...props}
            name="seller_before"
            disabled={charge.disabled}
            onAmountChange={onChargePaidByAmountChange}
          />
        </TitleWithValue>
      )}

      {isOrderTypeNotHUD1 && (
        <TitleWithValue title={t('charges:by-others')}>
          <Box className={classes.byOthersRow}>
            <AmountField
              {...props}
              name="by_others"
              disabled={charge.disabled}
              onAmountChange={onChargePaidByAmountChange}
            />
            {!charge.disabled && (
              <FontAwesomeIcon
                icon="user"
                style={{ cursor: 'pointer' }}
                onClick={() => onEditSplitAmount(charge, 'Payer')}
              />
            )}
          </Box>
        </TitleWithValue>
      )}

      <Box className={classes.expandControlRow}>
        <Button
          fullWidth
          aria-label="expand row"
          aria-expanded={isPartiesInvolvedRowOpen}
          onClick={togglePartiesinvolvedRowOpen}
          className={classes.expandControl}
          endIcon={
            <FontAwesomeIcon
              icon={isPartiesInvolvedRowOpen ? 'chevron-up' : 'chevron-down'}
              size="sm"
            />
          }
        >
          {t('orders:parties-involved')}
        </Button>
      </Box>

      <PartiesInvolvedSection
        open={isPartiesInvolvedRowOpen}
        charge={charge}
        onEditParties={onEditSplitAmount}
      />
    </Paper>
  );
};
